import { useState } from 'react'
import { ErrorBanner } from '@/components/AppLayout'
import { removeEventAttendees, type RemoveAttendeesResult } from '@/lib/queries'

interface Props {
  eventId: string
  eventName: string
  attendeeCount: number
  submissionCount: number
  onRemoved: () => void
}

/**
 * Clearing the attendee list, typically after importing the wrong Eventbrite
 * event.
 *
 * Separate from Reset on the Overview tab, which also unpublishes the form and
 * clears its passcode. Here the form is left exactly as it is, because a wrong
 * import has nothing to do with a passcode the organiser may already have
 * handed out.
 */
export function RemoveAllAttendeesCard({
  eventId,
  eventName,
  attendeeCount,
  submissionCount,
  onRemoved,
}: Props) {
  const [open, setOpen] = useState(false)
  const [typed, setTyped] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<RemoveAttendeesResult | null>(null)

  const confirmed = typed.trim() === eventName.trim()

  async function handleRemove() {
    setBusy(true)
    setError(null)
    try {
      const result = await removeEventAttendees(eventId)
      setDone(result)
      setOpen(false)
      setTyped('')
      onRemoved()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove the attendees')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card card-danger">
      <h2>Remove all attendees</h2>

      {error !== null && <ErrorBanner message={error} />}

      {done !== null && (
        <p className="banner banner-success">
          Removed {done.attendees_removed} {done.attendees_removed === 1 ? 'attendee' : 'attendees'}
          {done.submissions_removed > 0 &&
            ` and ${String(done.submissions_removed)} ${done.submissions_removed === 1 ? 'submission' : 'submissions'}`}
          . The form is untouched.
        </p>
      )}

      {!open ? (
        <>
          <p className="field-hint">
            Clears the attendee list so you can import again — useful if you imported the wrong
            Eventbrite event. The form keeps its settings, passcode and published status.
          </p>
          <div className="row btn-group-stack">
            <button
              className="btn btn-danger"
              type="button"
              disabled={attendeeCount === 0}
              title={attendeeCount === 0 ? 'There are no attendees to remove' : undefined}
              onClick={() => {
                setOpen(true)
                setDone(null)
              }}
            >
              Remove all {attendeeCount} attendees
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="banner banner-danger">
            This deletes all {attendeeCount} attendees and cannot be undone.
            {submissionCount > 0 && (
              <>
                {' '}
                It also deletes{' '}
                <strong>
                  {submissionCount} form {submissionCount === 1 ? 'submission' : 'submissions'}
                </strong>{' '}
                and any matches, because those belong to the attendees being removed. Those answers
                cannot be recreated.
              </>
            )}
          </p>

          {submissionCount > 0 && (
            <p className="field-hint">
              Only an organization owner can do this once people have submitted.
            </p>
          )}

          <div className="field">
            <label className="field-label" htmlFor="confirm-attendees">
              Type <strong>{eventName}</strong> to confirm
            </label>
            <input
              id="confirm-attendees"
              type="text"
              value={typed}
              onChange={(e) => {
                setTyped(e.target.value)
              }}
              autoComplete="off"
              autoFocus
            />
          </div>

          <div className="row btn-group-stack">
            <button
              className="btn btn-danger"
              type="button"
              disabled={busy || !confirmed}
              onClick={() => void handleRemove()}
            >
              {busy ? 'Removing…' : 'Remove all attendees'}
            </button>
            <button
              className="btn"
              type="button"
              disabled={busy}
              onClick={() => {
                setOpen(false)
                setTyped('')
              }}
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  )
}
